class TestimonialsCarousel {
  constructor(element) {
    this.carousel = element;
    this.track = this.carousel.querySelector('.testimonials-carousel__track');
    this.cards = Array.from(this.track.querySelectorAll('.testimonial-card'));
    this.prevButton = this.carousel.parentElement.querySelector('.testimonials-carousel__arrow--prev');
    this.nextButton = this.carousel.parentElement.querySelector('.testimonials-carousel__arrow--next');
    this.dots = Array.from(this.carousel.parentElement.querySelectorAll('.testimonials-carousel__dot'));

    this.currentIndex = 0;
    this.autoplayInterval = parseInt(this.carousel.dataset.autoplayInterval) || 5000;
    this.autoplayTimer = null;
    this.isDesktop = window.innerWidth >= 750;

    this.init();
  }

  init() {
    // Set initial state
    this.updateCarousel();
    this.updateDots();

    // Bind event listeners
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prev());
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next());
    }

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Handle window resize
    window.addEventListener('resize', () => this.handleResize());

    // Start autoplay
    this.startAutoplay();

    // Pause autoplay on hover
    this.carousel.parentElement.addEventListener('mouseenter', () => this.stopAutoplay());
    this.carousel.parentElement.addEventListener('mouseleave', () => this.startAutoplay());

    // Pause autoplay on focus (for accessibility)
    this.carousel.addEventListener('focusin', () => this.stopAutoplay());
    this.carousel.addEventListener('focusout', () => this.startAutoplay());
  }

  handleResize() {
    const wasDesktop = this.isDesktop;
    this.isDesktop = window.innerWidth >= 750;

    // Reset position if viewport changed between mobile/desktop
    if (wasDesktop !== this.isDesktop) {
      this.currentIndex = 0;
      this.updateCarousel();
      this.updateDots();
    }
  }

  getVisibleCards() {
    // Show 3 cards on desktop, 1 on mobile
    return this.isDesktop ? 3 : 1;
  }

  getMaxIndex() {
    const visibleCards = this.getVisibleCards();
    // On desktop (showing 3), max index is total - 3
    // On mobile (showing 1), max index is total - 1
    return Math.max(0, this.cards.length - visibleCards);
  }

  prev() {
    // Move by the number of visible cards for clean transitions
    const step = this.getVisibleCards();
    this.currentIndex = Math.max(0, this.currentIndex - step);
    this.updateCarousel();
    this.updateDots();
    this.resetAutoplay();
  }

  next() {
    const maxIndex = this.getMaxIndex();
    const step = this.getVisibleCards();

    // Loop back to start if at the end
    if (this.currentIndex >= maxIndex) {
      this.currentIndex = 0;
    } else {
      this.currentIndex = Math.min(this.currentIndex + step, maxIndex);
    }

    this.updateCarousel();
    this.updateDots();
    this.resetAutoplay();
  }

  goToSlide(index) {
    this.currentIndex = Math.min(index, this.getMaxIndex());
    this.updateCarousel();
    this.updateDots();
    this.resetAutoplay();
  }

  updateCarousel() {
    if (!this.cards.length) return;

    const cardWidth = this.cards[0].offsetWidth;
    const gap = this.isDesktop ? 24 : 20;
    const offset = -(this.currentIndex * (cardWidth + gap));

    this.track.style.transform = `translateX(${offset}px)`;

    // Update button states
    const maxIndex = this.getMaxIndex();
    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
      this.prevButton.style.opacity = this.currentIndex === 0 ? '0.3' : '1';
    }
    if (this.nextButton) {
      this.nextButton.disabled = this.currentIndex >= maxIndex;
      this.nextButton.style.opacity = this.currentIndex >= maxIndex ? '0.3' : '1';
    }
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      const dotIndex = parseInt(dot.dataset.index);

      // Highlight only the current index dot
      const isActive = dotIndex === this.currentIndex;

      dot.classList.toggle('active', isActive);
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.next();
    }, this.autoplayInterval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resetAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

// Image Modal Handler
class TestimonialImageModal {
  constructor() {
    this.modal = document.getElementById('testimonial-image-modal');
    if (!this.modal) return;

    this.modalImage = this.modal.querySelector('.testimonial-image-modal__image');
    this.closeButton = this.modal.querySelector('.testimonial-image-modal__close');

    this.init();
  }

  init() {
    // Bind thumbnail clicks
    document.addEventListener('click', (e) => {
      const thumbnail = e.target.closest('[data-testimonial-thumbnail]');
      if (thumbnail) {
        this.openModal(thumbnail);
      }
    });

    // Handle keyboard for thumbnails
    document.addEventListener('keydown', (e) => {
      const thumbnail = e.target.closest('[data-testimonial-thumbnail]');
      if (thumbnail && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        this.openModal(thumbnail);
      }
    });

    // Close on button click
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.closeModal();
      });
    }

    // Close on backdrop click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeModal();
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.closeModal();
      }
    });
  }

  openModal(thumbnail) {
    const fullImageUrl = thumbnail.dataset.fullImage;
    const img = thumbnail.querySelector('img');
    const altText = img?.alt || 'Customer photo';

    if (this.modalImage) {
      this.modalImage.src = fullImageUrl;
      this.modalImage.alt = altText;
    }

    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Focus close button for accessibility
    if (this.closeButton) {
      setTimeout(() => this.closeButton.focus(), 100);
    }
  }

  closeModal() {
    this.modal.classList.remove('active');
    document.body.style.overflow = '';

    // Clear image to prevent flash on next open
    if (this.modalImage) {
      setTimeout(() => {
        this.modalImage.src = '';
      }, 300);
    }
  }
}

// Initialize all carousels on the page
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('[data-carousel]');
  carousels.forEach(carousel => new TestimonialsCarousel(carousel));

  // Initialize image modal
  new TestimonialImageModal();
});

// Re-initialize on Shopify section load (theme editor)
if (Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const carousel = event.target.querySelector('[data-carousel]');
    if (carousel) {
      new TestimonialsCarousel(carousel);
    }
  });
}
